'use client';

import Link from 'next/link';
import { alpha, lighten, useTheme } from '@mui/material/styles';
import { useResolvedColorScheme } from '@/hooks/useResolvedColorScheme';
import { black, blue, white } from '@/lib/colours';
import {
  registrySidebarDarkRailForeground,
  registrySidebarLightRailForeground,
} from '@/lib/registrySidebarRail';
import { Button, type ButtonProps } from '@/primitives';
import { useShell } from '@/providers';
import { registrySidebarDarkBackground } from '@/lib/registrySidebarDarkBg';

export interface NavButtonLinkProps extends ButtonProps {
  isSubnav?: boolean;
  selected?: boolean;
}

export function NavButtonLink({
  isSubnav,
  selected,
  disabled,
  onClick,
  ...props
}: NavButtonLinkProps) {
  const theme = useTheme();
  const { isDarkMode } = useResolvedColorScheme();
  const { sidebarExpanded } = useShell();
  const railCollapsed = !sidebarExpanded && !isSubnav;

  const selectedBackground = isDarkMode
    ? lighten(registrySidebarDarkBackground(theme), 0.12)
    : !isSubnav
      ? blue[600]
      : blue[950];

  const selectedHoverBackground = isDarkMode
    ? lighten(registrySidebarDarkBackground(theme), 0.18)
    : undefined;

  const defaultLabelColor = isDarkMode
    ? registrySidebarDarkRailForeground
    : registrySidebarLightRailForeground;
  const selectedLabelColor = isDarkMode
    ? white[900]
    : registrySidebarLightRailForeground;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    onClick?.(e);
  };

  return (
    <Button
      {...props}
      disabled={disabled}
      onClick={handleClick}
      component={disabled ? 'button' : Link}
      sx={{
        justifyContent: railCollapsed ? 'center' : 'flex-start',
        px: railCollapsed ? '4px' : isSubnav ? undefined : '5px',
        ...(isSubnav && !railCollapsed ? { pl: '8px', pr: '6px' } : {}),
        py: '4px',
        ...(isSubnav
          ? {
              width: '100%',
              maxWidth: '100%',
              mx: 0,
            }
          : {
              width: 'calc(100% - 16px)',
              maxWidth: '100%',
              mx: 'auto',
            }),
        minWidth: railCollapsed ? 0 : undefined,
        boxSizing: 'border-box',
        color: selected ? selectedLabelColor : defaultLabelColor,
        textTransform: 'capitalize',
        ...(selected
          ? {
              background: selectedBackground,
              '&:hover': {
                background: isDarkMode
                  ? selectedHoverBackground
                  : alpha(selectedBackground, 0.85),
              },
            }
          : {
              '&:hover': {
                background: disabled
                  ? 'transparent'
                  : isDarkMode
                    ? alpha(white[900], 0.08)
                    : alpha(black[600], 0.25),
              },
            }),
        ...(disabled && {
          opacity: 0.5,
          cursor: 'not-allowed',
        }),
        '.MuiButton-startIcon': {
          padding: railCollapsed ? '2px' : '3px',
          margin: railCollapsed ? 0 : undefined,
          color: 'inherit',
          svg: {
            fontSize: '18px',
          },
        },
        '.MuiButton-endIcon': {
          marginLeft: 'auto',
          color: 'inherit',
        },
      }}
    />
  );
}
